import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { GenerateRoofOptions, RoofGenerationStage } from "building-types/roofs/roofGenerationTypes";
import { generatedMultiGableRoofNew } from "./generateEmptyRoof";
import { generateRoofBasicParameters } from "./generateRoofBasicParameters";
import { generateRoofBottomFaces } from "./generateRoofBottomFaces";
import { generateRoofEaveBrackets } from "./generateRoofEaveBrackets";
import { generateRoofGableClipping } from "./generateRoofGableClipping";
import { generateRoofPerimeterRails } from "./generateRoofPerimeterRails";
import { generateRoofTopFaces } from "./generateRoofTopFaces";
import { generateRoofWallFaces } from "./generateRoofWallFaces";
import { generateRoofWindows } from "./generateRoofWindows";
import { roofGenerationTemporariesNew } from "./roofGeneration";

const roofGenerationStages: RoofGenerationStage[] = [
  generateRoofBasicParameters,
  generateRoofWallFaces,
  generateRoofBottomFaces,
  generateRoofWindows,
  generateRoofPerimeterRails,
  generateRoofTopFaces,
  generateRoofGableClipping,
  generateRoofEaveBrackets,
];

/**
 * Creates a multi-gable roof. This executes the different
 * stages of the roof generation process. Each stage receives the input options and
 * the current state of the roof, and returns new fields to be merged into the generated
 * roof. A object of temporary values is also passed between stages for storing intermidiate
 * data that is not part of the generated roof.
 */
export async function generateMultiGableRoof(subRoofOptions: GenerateRoofOptions): Promise<GeneratedRoof> {
  // create an empty roof
  const subRoof = generatedMultiGableRoofNew(subRoofOptions);

  // create empty temporary values
  const temporaries = roofGenerationTemporariesNew();

  // execute each stage of the roof generation process
  for (const stage of roofGenerationStages) {
    // Only for explicit false (e.g. dormer sub-roofs when wallPlanes is on). Omitted/undefined: unchanged.
    if (subRoofOptions.addRoofWallFaces === false && stage === generateRoofWallFaces) {
      continue;
    }

    const { subRoof: newSubRoof, temporaries: newTemporaries = {} } = await stage({
      subRoofOptions,
      subRoof,
      temporaries,
    });

    Object.assign(subRoof, newSubRoof);
    Object.assign(temporaries, newTemporaries);
  }

  return subRoof;
}
